/**
 * Single source of truth for product data — public/content/products-content.json.
 * Shared by the products page and the desktop/mobile nav menus, so the
 * product lineup only has to be edited in one place.
 */
export interface Product {
  id: string;
  prefix: string;
  name: string;
  description: string;
  language?: string;
  tags: string[];
  icon?: string;
  initial?: string;
  /** Short display badge (e.g. the product's own domain), when the JSON provides one. */
  badge?: string;
  link: string;
  featured?: boolean;
  /** Open-source bento column span on desktop (2 = wide tile). */
  span?: number;
}

export interface ProductCollection {
  id: string;
  sectionId: string;
  title: string;
  /** Short label for the narrow mobile tab bar; falls back to `title`. */
  mobileTitle?: string;
  subtitle: string;
  badge?: string;
  icon?: string;
  products: Product[];
}

// Shape of public/content/products-content.json, so the fetch is type-checked
// instead of `any` and a malformed/partial file surfaces in the editor.
export interface ProductsContent {
  meta_tags: {
    title: string;
    meta: { hid?: string; name: string; content: string }[];
  };
  collections: ProductCollection[];
  ecosystem: {
    heading: string;
    subcopy: string;
    items: {
      icon: string;
      label: string;
      title: string;
      text: string;
      products: string[];
    }[];
  };
  faq: {
    heading: string;
    subcopy: string;
    items: { q: string; group?: string; a: string }[];
  };
}

export async function useProductsData() {
  const url = useRequestURL();
  const { data, error } = await useFetch<ProductsContent>(
    `${url.origin}/content/products-content.json`,
    { key: "products-content" }
  );

  // The products page throws on this error itself; the nav menus only read
  // collections, so without a log a 404/partial deploy would degrade to an
  // empty menu with no trace. Surface it with context here, at the source.
  if (error.value) {
    console.error(
      "[products] failed to load products-content.json (status:",
      error.value.status,
      ")",
      error.value
    );
  }

  const collections = computed<ProductCollection[]>(
    () => data.value?.collections ?? []
  );

  const getCollection = (id: string) =>
    collections.value.find((c) => c.id === id);

  const openSource = computed<Product[]>(
    () => getCollection("open-source")?.products ?? []
  );
  const enterprise = computed<Product[]>(
    () => getCollection("enterprise")?.products ?? []
  );
  const ai = computed<Product[]>(
    () => getCollection("ai")?.products ?? []
  );

  // Flagship lineup shown in the hero — products flagged "featured" in the JSON.
  const flagship = computed<Product[]>(() =>
    collections.value.flatMap((c) => c.products).filter((p) => p.featured)
  );

  return { data, error, collections, openSource, enterprise, ai, flagship };
}
